/**
 * Utility function to download certificate PDF from backend
 */

import { getToken } from 'dmsbasereact';
import { getBaseURL } from "../build_config";

/**
 * Opens a certificate PDF in print mode
 * @param {number} certificatePk - The primary key of the certificate
 * @returns {Promise<boolean>} - Returns true if successful
 */
export const openCertificarePdfForPrint = async (certificatePk) => {
  try {
    const token = getToken();
    const url = `${getBaseURL()}/training_material/certificate/${certificatePk}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
        'Accept': 'application/pdf'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      window.URL.revokeObjectURL(blobUrl);
      console.error('Failed to open window - popup blocker may be active');
      return false;
    }

    printWindow.location.href = blobUrl;

    printWindow.onload = () => {
      setTimeout(() => {
        try {
          printWindow.print();
        } catch (e) {
          console.error('Error triggering print:', e);
        }
      }, 500);
    };

    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 60000);

    return true;
  } catch (error) {
    console.error('Error opening certificate PDF for print:', error);
    return false;
  }
};

/**
 * Downloads a certificate PDF file
 * @param {number} certificatePk - The primary key of the certificate
 * @returns {Promise<boolean>} - Returns true if download was successful
 */
export const downloadCertificatePdf = async (certificatePk) => {
  try {
    const token = getToken();
    const url = `${getBaseURL()}/training_material/certificate/${certificatePk}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token.access_token}`,
        'Accept': 'application/pdf'
      }
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Get the blob from response
    const blob = await response.blob();

    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'certificate.pdf';

    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }

    // Create a temporary URL for the blob
    const blobUrl = window.URL.createObjectURL(blob);

    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);

    return true;
  } catch (error) {
    console.error('Error downloading certificate PDF:', error);
    return false;
  }
};
